import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
    listDomainIntelligence,
    getDomainIntelligenceFile,
    updateDomainIntelligenceFile,
} from './domainIntelligenceApi'

export const useDomainIntelligence = (workspaceID, filePath) => {
    const queryClient = useQueryClient()

    // ==========================================================================
    // Queries
    // ==========================================================================

    const {
        data: filesData,
        isLoading: loadingFiles,
        error: filesError,
        refetch: refetchFiles,
    } = useQuery({
        queryKey: ['domainIntelligenceFiles', workspaceID],
        queryFn: () => listDomainIntelligence(workspaceID),
        enabled: Boolean(workspaceID),
    })

    const {
        data: fileContentData,
        isLoading: loadingFileContent,
        error: fileContentError,
        refetch: refetchFileContent,
    } = useQuery({
        queryKey: ['domainIntelligenceFile', workspaceID, filePath],
        queryFn: () => getDomainIntelligenceFile(workspaceID, filePath),
        enabled: Boolean(workspaceID) && Boolean(filePath),
    })

    // ==========================================================================
    // Mutations
    // ==========================================================================

    const { mutate: updateFile, isPending: isUpdating } = useMutation({
        mutationFn: ({ filePath: path, content }) =>
            updateDomainIntelligenceFile(workspaceID, path, content),
        onSuccess: (response, variables) => {
            queryClient.invalidateQueries({
                queryKey: ['domainIntelligenceFiles', workspaceID],
            })
            queryClient.invalidateQueries({
                queryKey: ['domainIntelligenceFile', workspaceID, variables.filePath],
            })
            if (variables.onSuccess) {
                variables.onSuccess(response)
            }
        },
        onError: (error, variables) => {
            if (variables.onError) {
                variables.onError(error)
            }
        },
    })

    // ==========================================================================
    // Return all data and functions
    // ==========================================================================

    return {
        // File list
        files: filesData?.files || [],
        loadingFiles,
        filesError,
        refetchFiles,

        // File content
        fileContent: fileContentData?.content || null,
        loadingFileContent,
        fileContentError,
        refetchFileContent,

        // Mutations
        updateFile,
        isUpdating,
    }
}
